import React, { FunctionComponent, useCallback, useMemo, useRef, useEffect } from 'react';
import { ArrowBackIcon, Grid, makeStyles, Typography } from '@rbx/ui';
import { useLocalization, useTranslation } from '@rbx/intl';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { leftNavID } from '@modules/layout/layout/constants/layoutConstants';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import useSideBarContent from '../hooks/useSideBarContent';
import TreeList from './TreeList';
import SidebarFilter from './SidebarFilter';
import {
  useSidebarFilterQuery,
  useFilteredTreeData,
  DEBOUNCE_DELAY_MILLISECONDS,
} from '../hooks/useSidebarFilterQuery';
import useSideBarNavigationStyles from './SideBarNavigation.styles';

export const translationKeyNoResultsFound = 'Label.NoResultsFound';

const useStyles = makeStyles()(() => ({
  heading: {
    fontSize: '14px',
  },
  tree: {
    '& *': {
      fontSize: '16px',
    },
  },

  treeList: {
    marginTop: '20px',
    '&:first-child': {
      marginTop: 0,
    },
  },

  noResults: {
    marginTop: '16px',
  },
  backToButton: {
    marginTop: '4px',
    width: '100%',
    justifyContent: 'flex-start',
  },
}));

const SideBarContent: FunctionComponent<React.PropsWithChildren> = () => {
  const { translate } = useTranslation();
  const { locale } = useLocalization();
  const { enableNewLayout } = useAppConfig();
  const {
    selected,
    setSelected,
    expanded,
    setExpanded,
    treeListData,
    backTo,
    hasEngineClasses,
    sidebarSessionId,
    resetSidebarSessionId,
  } = useSideBarContent();
  const { filterQuery, setFilterQuery, debouncedFilterQuery, resetFilterQuery } =
    useSidebarFilterQuery(resetSidebarSessionId);
  const {
    classes: { tree, heading: headingStyles, treeList, noResults, backToButton },
  } = useStyles();
  const {
    cx,
    classes: { sidebarContent, iaSidebarContent, stickySidebarContent, iaStickySidebarContent },
  } = useSideBarNavigationStyles();
  const filterFieldRef = useRef<HTMLInputElement>(null);

  const eventParameters = useMemo(() => {
    return {
      locale: locale ?? '',
      refPageUrl: typeof window !== 'undefined' ? window.location.pathname : '',
      sidebarSessionId,
    };
  }, [locale, sidebarSessionId]);

  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilterQuery(e.target.value);
      setTimeout(() => {
        unifiedLoggerClient.logClickEvent({
          eventName: DocsSiteEventType.SideBarFilterPerformed,
          parameters: {
            ...eventParameters,
            filterQuery: debouncedFilterQuery,
          },
        });
      }, DEBOUNCE_DELAY_MILLISECONDS);
    },
    [setFilterQuery, eventParameters, debouncedFilterQuery],
  );

  const handleClearFilter = useCallback(() => {
    resetFilterQuery();
    filterFieldRef.current?.focus();
    unifiedLoggerClient.logClickEvent({
      eventName: DocsSiteEventType.SideBarFilterCleared,
      parameters: {
        ...eventParameters,
        filterQuery: debouncedFilterQuery,
      },
    });
  }, [resetFilterQuery, eventParameters, debouncedFilterQuery]);

  useEffect(() => {
    // Clear filter when hasEngineClasses changes to false
    if (!hasEngineClasses && filterQuery) {
      resetFilterQuery();
    }
  }, [hasEngineClasses, filterQuery, resetFilterQuery]);

  const { data: filteredTreeListData } = useFilteredTreeData(
    treeListData,
    debouncedFilterQuery,
    setExpanded,
  );

  const memoizedTreeListData = useMemo(() => filteredTreeListData, [filteredTreeListData]);

  return (
    <Grid
      id={leftNavID}
      container
      className={cx(sidebarContent, { [iaSidebarContent]: enableNewLayout })}>
      <span
        className={cx({
          [stickySidebarContent]: hasEngineClasses,
          [iaStickySidebarContent]: enableNewLayout,
        })}>
        {backTo && (
          <DynamicallyRouted.Button
            href={backTo.path}
            size='small'
            color='primary'
            variant='text'
            className={backToButton}
            startIcon={<ArrowBackIcon />}>
            {backTo.title}
          </DynamicallyRouted.Button>
        )}
        {hasEngineClasses && (
          <SidebarFilter
            filterQuery={filterQuery}
            onFilterChange={handleFilterChange}
            onClearFilter={handleClearFilter}
            inputRef={filterFieldRef}
            translate={translate}
          />
        )}
      </span>
      {memoizedTreeListData.map(({ heading, nodes }) => (
        <TreeList
          treeClassName={tree}
          className={treeList}
          headingClassName={headingStyles}
          key={`${heading || 'no-heading'}-${nodes[0]?.id || 'no-nodes'}`}
          heading={heading}
          treeNodes={nodes}
          expanded={expanded}
          selected={selected}
          setExpanded={setExpanded}
          setSelected={setSelected}
        />
      ))}
      {memoizedTreeListData.length === 0 && (
        <Typography variant='body1' color='secondary' className={noResults}>
          {translate(translationKeyNoResultsFound)}
        </Typography>
      )}
    </Grid>
  );
};

export default SideBarContent;
